import { useState, useCallback, useEffect } from "react";
import {
  startOfDay,
  endOfDay,
  format,
  startOfMonth,
  endOfMonth,
  subDays,
} from "date-fns";
import { Calendar as CalendarIcon } from "lucide-react";

import { cn } from "@/lib/utils";
import { Button } from "@shared/components/ui/button";
import { Calendar } from "@shared/components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@shared/components/ui/popover";
import { useTranslation } from "react-i18next";

const DATE_FORMATS = {
  singleDate: "LL/dd/y",
  dateRange: "LL/dd/y",
};

const QUICK_SELECT_OPTIONS = [
  {
    label: "datePicker.today",
    handler: () => {
      const today = new Date();
      return { from: startOfDay(today), to: endOfDay(today) };
    },
  },
  {
    label: "datePicker.lastSevenDays",
    handler: () => {
      const today = new Date();
      return { from: subDays(today, 6), to: today };
    },
  },
  {
    label: "datePicker.lastThirtyDays",
    handler: () => {
      const today = new Date();
      return { from: subDays(today, 30), to: today };
    },
  },
  {
    label: "datePicker.thisMonth",
    handler: () => {
      const today = new Date();
      return { from: startOfMonth(today), to: endOfMonth(today) };
    },
  },
  {
    label: "datePicker.allTime",
    handler: () => {
      const today = new Date();
      return { from: startOfDay(subDays(today, 1000)), to: endOfDay(today) };
    },
  },
  {
    label: "datePicker.custom",
    handler: () => null, // Special case for custom selection
  },
];

export function DatePickerWithRange({ className, onSelectDate, initialDate }) {
  const { t } = useTranslation();
  const [date, setDate] = useState(initialDate || { from: null, to: null });
  const [showCalendar, setShowCalendar] = useState(false);
  const [selectedOption, setSelectedOption] = useState(null);

  // Update local state when initialDate changes
  useEffect(() => {
    if (initialDate) {
      setDate(initialDate);
    }
  }, [initialDate]);

  const handleDateChange = useCallback(
    (newDate) => {
      setDate(newDate);
      // Only trigger onSelectDate callback when both start and end dates exist
      if (onSelectDate && newDate?.from && newDate?.to) {
        onSelectDate(newDate);
      }
    },
    [onSelectDate]
  );

  const handleQuickSelect = useCallback(
    (handler, optionIndex) => {
      const newDate = handler();
      setSelectedOption(optionIndex);

      if (newDate === null) {
        // Custom option selected - show calendar
        setShowCalendar(true);
      } else {
        // Predefined option selected - hide calendar and set date
        setShowCalendar(false);
        handleDateChange(newDate);
      }
    },
    [handleDateChange]
  );

  const handleCalendarDateChange = useCallback(
    (newDate) => {
      handleDateChange(newDate);
      // Don't reset selected option when using custom calendar if custom was selected
      if (selectedOption !== QUICK_SELECT_OPTIONS.length - 1) {
        setSelectedOption(null);
      }
      // Keep calendar open if custom is selected, even when both dates are selected
      if (
        newDate?.from &&
        newDate?.to &&
        selectedOption !== QUICK_SELECT_OPTIONS.length - 1
      ) {
        setShowCalendar(false);
      }
    },
    [handleDateChange, selectedOption]
  );

  const formatDateRange = useCallback(
    (dateRange) => {
      if (!dateRange?.from) {
        return t("datePicker.pickAdate");
      }
      if (!dateRange?.to) {
        return format(dateRange?.from, DATE_FORMATS.singleDate);
      }
      return `${format(dateRange?.from, DATE_FORMATS.dateRange)} - ${format(
        dateRange?.to,
        DATE_FORMATS.dateRange
      )}`;
    },
    [t]
  );

  return (
    <div className={cn("grid gap-2", className)}>
      <Popover>
        <PopoverTrigger asChild>
          <Button
            id="date"
            variant="outline"
            className={cn(
              "w-full justify-start text-left font-normal",
              !date?.from && "text-muted-foreground"
            )}
          >
            <CalendarIcon className="mr-2 h-4 w-4" />
            {formatDateRange(date)}
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0" align="start">
          {/* Quick Select */}
          <div className="p-3 border-b">
            <div className="grid grid-cols-2 gap-2">
              {QUICK_SELECT_OPTIONS.map((option, index) => (
                <Button
                  key={index}
                  variant={selectedOption === index ? "default" : "ghost"}
                  size="sm"
                  className="justify-start"
                  onClick={() => handleQuickSelect(option.handler, index)}
                >
                  {t(option.label)}
                </Button>
              ))}
            </div>
          </div>

          {/* Calendar */}
          {showCalendar && (
            <Calendar
              initialFocus
              mode="range"
              defaultMonth={date?.from}
              selected={date}
              // captionLayout="dropdown"
              onSelect={handleCalendarDateChange}
              numberOfMonths={2}
            />
          )}
        </PopoverContent>
      </Popover>
    </div>
  );
}
